"use client";

import { useId, useState } from "react";
import type { PasswordSignInFailure, PasswordSubmit } from "@/lib/password-sign-in";

/**
 * ONE SENTENCE PER REASON, and the mapping is the point of the whole file.
 *
 * `~/Developer/DESIGN.md` ("Ring sign-in") states the contract these come
 * from: the verify-password endpoint answers a wrong password, an unverified
 * account and an address nobody registered with ONE response, so exactly one
 * outcome — `denied` — may be shown as a password problem. The obvious
 * implementation, `if (!ok) "wrong password"`, tells a rate-limited person to
 * retype a password that is correct, for the full fifteen minutes of a lockout
 * they may have earned in one of the seven sibling apps that share the limit.
 *
 * A `Record` over the union rather than a `switch` with a default: a seventh
 * failure mode added to `PasswordSignInFailure` is a COMPILE ERROR here, not a
 * silent fallback to whichever sentence the default happened to hold.
 */
const MESSAGES: Record<PasswordSignInFailure, string> = {
  denied: "That email and password don’t match.",

  // Says nothing about which of the two was wrong, and nothing about whether
  // the address has an account — the endpoint does not know either, by design.
  rate_limited: "Too many attempts just now. Wait a few minutes, then try again.",

  /**
   * app_misconfigured and service_down GET THE SAME SENTENCE, deliberately.
   * They differ in whose configuration is broken — nexus's ring secret, or the
   * auth service's own — and a visitor can act on neither. What they can act
   * on is the Google button below, which is why the copy points at it.
   *
   * What this must NOT do is cast any doubt on the password. Both are nexus's
   * fault; a person told "check your password" while the fault is ours goes to
   * the reset flow and changes a password that was right all along.
   */
  app_misconfigured:
    "Password sign-in isn’t working right now. That’s on us, not your password. Continue with Google below, or try again later.",
  service_down:
    "Password sign-in isn’t working right now. That’s on us, not your password. Continue with Google below, or try again later.",

  // The request never produced an answer we understand: it may never have
  // arrived, or the reply may have been lost on the way back. Offers the retry
  // rather than a diagnosis, because this browser cannot tell those apart.
  unreachable: "We couldn’t reach the sign-in service. Check your connection and try again.",

  // The password was right and nexus's own admission gate still said no.
  // Never "that didn't match" — that would send somebody to reset a password
  // that is already correct. A refused GOOGLE sign-in lands on /invite/error
  // instead; this path has no session, so that page's "Sign out" branch would
  // be wrong for it, and the login page owes one error region in every state.
  not_admitted:
    "That password is right, but this account can’t sign in to Nexus. If someone invited you, open your invite link again.",
};

/**
 * The Crystal Prism password half of `/login`, beside the Google button that
 * `app/(auth)/login/page.tsx` renders under it.
 *
 * `submit` is a required PROP and the route hands it the server action. A form
 * that reached for the action itself would drag `@/auth` — and NextAuth's boot
 * and `@/db` with it — into this module's graph, which
 * `lib/db.clientBoundary.test.ts` walks and would red on. Same header, same
 * reason, as `auth.crystalprism.io/app/forgot/forgot-page.tsx`.
 */
export function LoginForm({ submit }: { submit: PasswordSubmit }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const id = useId();
  const emailId = `${id}-email`;
  const passwordId = `${id}-password`;

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;

    setPending(true);
    setMessage(null);

    let outcome;
    try {
      outcome = await submit(email, password);
    } catch {
      // The error VALUE is ignored on purpose: Next redacts a server action's
      // thrown message in production, so every rejection arrives as the same
      // generic string and reading it would render meaningless text. What is
      // true of every rejection is that this browser did not get an answer,
      // which is exactly what `unreachable` says.
      setMessage(MESSAGES.unreachable);
      setPending(false);
      return;
    }

    if (outcome.ok) {
      // A FULL PAGE LOAD, not a router push. The session cookie the action
      // just set is the one shared across .crystalprism.io
      // (lib/session-cookie.ts), and a hard navigation is what guarantees the
      // very next request carries it — no cached RSC payload rendered for a
      // signed-out visitor, and no dependence on a refresh landing in the
      // right order. `pending` is left ON: the page is on its way out, and
      // re-enabling the button would offer a second sign-in during the
      // navigation.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- deliberate hard navigation (see above)
      window.location.assign("/");
      return;
    }

    setMessage(MESSAGES[outcome.reason]);
    setPending(false);
  }

  return (
    <form className="auth-form" onSubmit={onSubmit}>
      {/* `auth-field`, NOT the fleet's `field`. In nexus `.field` is the fixed
          full-viewport sky gradient (globals.css, "Deep field gradient"), which
          AuthShell renders behind this card — a form row wearing that class
          would be positioned `fixed` across the whole screen. */}
      <div className="auth-field">
        <label className="auth-fld" htmlFor={emailId}>
          Email address
        </label>
        <input
          id={emailId}
          className="auth-input"
          name="email"
          type="email"
          autoComplete="email"
          spellCheck={false}
          required
          disabled={pending}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="auth-field">
        {/* Named for WHOSE password it is. nexus has never had one of its own,
            and the address someone uses here may well be an account they made
            in a sibling app — "Password" alone invites them to invent one. */}
        <label className="auth-fld" htmlFor={passwordId}>
          Crystal Prism password
        </label>
        <input
          id={passwordId}
          className="auth-input"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          disabled={pending}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      {/* Right-aligned under the password field, in the same place and the same
          words as account.crystalprism.io/sign-in, so somebody who has used any
          other Crystal Prism surface finds it where they already looked. A
          plain <a>, not next/link: both leave this app. */}
      <div className="auth-links">
        <a className="auth-recover auth-recover--start" href="https://auth.crystalprism.io/register?app=nexus">
          Create an account
        </a>
        <a className="auth-recover" href="https://auth.crystalprism.io/forgot?app=nexus">
          Forgot your password?
        </a>
      </div>

      {/* PRESENT IN EVERY STATE. The element carrying `role="status"` is
          rendered whether or not there is anything to say, so React reconciles
          it rather than remounting it and the live region exists before its
          content does — a region created at the same moment as its message
          announces nothing at all. It is a real box in the accessibility tree
          at all times: not `display: none`, not `visibility: hidden`, not
          `display: contents`, each of which takes it back out of that tree and
          reintroduces the defect in a form that looks fixed. Empty, it must
          also cost no space, which is what `.auth-live.is-quiet` in globals.css
          is for — geometry, not visibility.

          `role="status"` rather than `role="alert"`: status is polite and
          atomic, so the sentence is read once, after whatever the reader was
          already saying. An alert interrupts, and the message is never urgent
          — the person is looking straight at the form they just submitted. */}
      <div className={message ? "auth-live" : "auth-live is-quiet"} role="status">
        {message ? <p className="auth-error">{message}</p> : null}
      </div>

      {/* `.new` is nexus's one primary button — gold, Space Mono, uppercase.
          `.is-busy` + `.busy-star` is the app's existing busy convention
          (components/ActionButton.tsx), reproduced here rather than imported
          because that component owns a click handler this form does not want:
          the submit belongs to the <form>, so Enter in either field works.

          THE LABEL DOES NOT CHANGE while the attempt is in flight, which is a
          deliberate departure from giftme's "Signing in…". ActionButton's own
          contract is "the label dims in place (no width change — the star
          overlays, it doesn't push)", and every other async button in nexus
          behaves that way; a login button that reflowed instead would be the
          one exception in the app. The state is carried by `disabled` and
          `aria-busy`, which is what a button's work-in-progress is spelled as,
          plus the pulsing ✦ — the same pair every ActionButton ships. */}
      <button
        type="submit"
        className={pending ? "new auth-submit is-busy" : "new auth-submit"}
        disabled={pending}
        aria-busy={pending}
      >
        <span className="busy-inner">Sign in</span>
        {pending ? (
          <span className="busy-star" aria-hidden="true">
            ✦
          </span>
        ) : null}
      </button>
    </form>
  );
}
